import React, { createContext, PropsWithChildren, useCallback, useEffect, useReducer } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import WithQuery from 'components/layout/WithQuery';
import useGuessTheOrderStore from 'graph/state/query-hooks/use-guess-the-order-store';
import useGuessWhereStore from 'graph/state/query-hooks/use-guess-where-store';
import useGuessWhoStore from 'graph/state/query-hooks/use-guess-who-store';
import useFeelingsMatchStore from 'graph/state/query-hooks/use-feelings-match-store';
import useAttemptStore from '@monorepo/shared/src/graph/state/query-hooks/use-attempt-store';
import NextButton from '@monorepo/shared/src/components/common/Buttons/NextButton';
import ActivityCompletion from 'components/modules/Activities/components/ActivityCompletion';
import useTelemetry from '@monorepo/shared/src/hooks/use-telemetry';
import { TelemetryEvents } from '@monorepo/shared/src/types';
import { ActivityTypes, ButtonSize } from 'types';
import {
  GuessWhereActivity,
  GuessWhereRound,
  GuessWhoRound,
  AttemptStatus,
} from '@monorepo/shared/src/graph/schema-generated-types-student';
import { ActivityActions, ActivityActionTypes, ActivityState } from './types';
import { defaultState, reducers } from './reducers';
import { formatCharacter } from '@monorepo/shared/src/util/telemetry-util';

const activityTypeStoreMap = {
  [ActivityTypes.GUESS_THE_ORDER]: useGuessTheOrderStore,
  [ActivityTypes.GUESS_WHERE]: useGuessWhereStore,
  [ActivityTypes.GUESS_WHO]: useGuessWhoStore,
  [ActivityTypes.FEELINGS_MATCH]: useFeelingsMatchStore,
};

type Props = {
  activity: string;
};

export const ActivityContext = createContext<any>([]);

let studentReward;

export const ActivityProvider: React.FC<PropsWithChildren<Props>> = ({ activity, children }) => {
  const [, { fireEvent }] = useTelemetry(TelemetryEvents.ACCESS_ACTIVITY);
  const [state, dispatch] = useReducer<React.Reducer<ActivityState, ActivityActions>>(
    reducers,
    defaultState()
  );

  const { search } = useLocation();

  const query = new URLSearchParams(search);
  const bookCode = query.get('bookCode');

  const [activityData] = activityTypeStoreMap[activity](bookCode);

  const { assignmentActivityId } = useParams();

  const [, { handleSaveAttempt }] = useAttemptStore();
  useEffect(() => {
    if (activityData?.activity?.book) {
      const { characters } = activityData.activity.book;
      fireEvent({
        activityType: activity,
        bookCode,
        character: formatCharacter(characters[0]),
      });
    }
  }, [activityData?.activity?.book]);

  useEffect(() => {
    if (activityData.isLoading) return;
    dispatch({
      type: ActivityActionTypes.SET_ACTIVITY,
      payload: activityData.activity,
    });
    dispatch({
      type: ActivityActionTypes.SET_CURRENT_ROUND,
      payload: {
        round: 1,
        data: (activityData.activity.rounds || [])[0] || {
          tiles: activityData.activity.storyElements,
        },
      },
    });
    dispatch({
      type: ActivityActionTypes.SET_IS_LAST_ROUND,
      payload: !activityData.activity.rounds || activityData.activity.rounds.length === 1,
    });
  }, [activityData.isLoading]);

  const loadNextRound = useCallback(
    (round: number, data: GuessWhereRound | GuessWhoRound) => {
      dispatch({
        type: ActivityActionTypes.SET_CURRENT_ROUND,
        payload: { round, data },
      });
      dispatch({
        type: ActivityActionTypes.SET_IS_LAST_ROUND,
        payload: round,
      });
    },
    [dispatch]
  );

  const setIsActivityOver = (isActivityOver: boolean) => {
    dispatch({
      type: ActivityActionTypes.SET_IS_ACTIVITY_OVER,
      payload: isActivityOver,
    });
  };

  const setShowNextButton = (showNextButton: boolean) => {
    dispatch({
      type: ActivityActionTypes.SET_SHOW_NEXT_BUTTON,
      payload: showNextButton,
    });
  };

  const setShowAffirmation = (showAffirmation: boolean) => {
    dispatch({
      type: ActivityActionTypes.SET_SHOW_AFFIRMATION,
      payload: showAffirmation,
    });
  };

  const hideTargetsFromScene = useCallback(
    (sceneContainerRef: React.RefObject<HTMLDivElement>, targetClasses?: string) => {
      // Retrieve the correct tile selectors per the current round
      const correctTileSelectors = (state?.activity?.rounds as GuessWhereActivity['rounds']).reduce(
        (agg, round, index) => {
          if (index && index + 1 >= (state?.currentRound?.round || 1)) {
            agg = agg.concat(
              round?.tiles
                .filter((tile) => tile.image.correct)
                .map((tile) => tile.image.elementId?.toLowerCase()) as string[]
            );
          }

          return agg;
        },
        [] as string[]
      );
      // Retrieve the svg targets to hide per their id selector
      const targets = sceneContainerRef?.current?.querySelectorAll(
        correctTileSelectors.map((correctTileDescription) => `#${correctTileDescription}`).join()
      );

      // Hide svg targets
      targets?.forEach((element) => {
        targetClasses && element.setAttribute('class', targetClasses);
        element.setAttribute('style', 'display: none');
      });
    },
    [dispatch, state]
  );

  const showSceneTarget = useCallback(
    (
      sceneContainerRef: React.RefObject<HTMLDivElement>,
      idSelector: string,
      targetClasses?: string
    ) => {
      // Retrieve the svg targets to show per their id selector
      const targets = sceneContainerRef?.current?.querySelectorAll(idSelector);

      // Show svg targets
      targets?.forEach((element) => {
        targetClasses && element.setAttribute('class', targetClasses);
        element.setAttribute('style', `display: auto`);
      });
    },
    [dispatch]
  );

  const handleEndOfActivity = useCallback(async () => {
    setIsActivityOver(true);
    setShowNextButton(true);
  }, [assignmentActivityId]);

  useEffect(() => {
    const sendCompleteAttempt = async () => {
      const result = await handleSaveAttempt({
        assignmentActivityId: +assignmentActivityId!,
        status: AttemptStatus.COMPLETED,
        dateCompleted: new Date(),
      });
      studentReward = result?.studentReward;
    };
    if (state.isActivityOver) {
      sendCompleteAttempt();
    }
  }, [state.isActivityOver]);

  useEffect(() => {
    assignmentActivityId &&
      handleSaveAttempt({
        assignmentActivityId: JSON.parse(assignmentActivityId),
        status: AttemptStatus.IN_PROGRESS,
        dateStarted: new Date(),
      });
  }, [assignmentActivityId]);

  return (
    <ActivityContext.Provider
      value={[
        state,
        {
          loadNextRound,
          hideTargetsFromScene,
          showSceneTarget,
          handleEndOfActivity,
          setIsActivityOver,
          setShowNextButton,
        },
      ]}
    >
      <WithQuery loading={activityData.isLoading} error={activityData.error}>
        {state.showAffirmation && (
          <ActivityCompletion studentReward={studentReward} skipAttemptUpdate />
        )}
        {children}
        {state.showNextButton && (
          <NextButton onClick={() => setShowAffirmation(true)} size={ButtonSize.SMALL} pulsing />
        )}
      </WithQuery>
    </ActivityContext.Provider>
  );
};

export default ActivityProvider;
